import React, { useState, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    MenuItem,
    Chip,
    CircularProgress,
    Divider,
    Alert,
    Tooltip,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Business as BusinessIcon,
    AddCircle as AddCircleIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { useCollaborators } from "../../../../hooks/administration/useServiceCollaborators";
import { useOrganizations } from "../../../../hooks/useOrganizations";

const CollaboratorsModal = ({ open, handleClose, serviceId, showAlert }) => {
    // Hooks de datos operativos vinculados a la OS
    const {
        colaboradores,
        isLoading: loadingCollaborators,
        addCollaboratorMutation,
        deleteCollaboratorMutation,
        isBusy: collaboratorsBusy
    } = useCollaborators(serviceId, { enabled: open });

    // 🎯 Invocación del hook maestro de organizaciones del sistema para seguir el estándar
    const {
        organizations,
        isLoading: loadingOrganizations
    } = useOrganizations();

    // Estados locales para el formulario de adición
    const [selectedOrgId, setSelectedOrgId] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);

    // Unificación de estados de carga y bloqueo
    const isMasterLoading = loadingCollaborators || loadingOrganizations;
    const isComponentBusy = collaboratorsBusy || addCollaboratorMutation.isPending || deleteCollaboratorMutation.isPending;

    // 🎯 Filtrado dinámico en memoria usandouseMemo (Cero llamadas Axios nativas)
    // Filtra las organizaciones maestras para obtener solo las de tipo PROVEEDOR activas
    const providersCatalogue = useMemo(() => {
        return (organizations || []).filter(
            org => org.orgtip === "PROVEEDOR" && org.estreg === "A"
        );
    }, [organizations]);

    // Evita listar en el combo de adición a los proveedores que ya están asignados a esta OS
    const availableOptions = useMemo(() => {
        const assignedOrgIds = colaboradores.map(c => Number(c.orgid));
        return providersCatalogue.filter(
            option => !assignedOrgIds.includes(Number(option.orgid))
        );
    }, [providersCatalogue, colaboradores]);

    const handleAddCollaborator = () => {
        if (!selectedOrgId) {
            showAlert("Debe seleccionar una organización proveedora.", "error");
            return;
        }

        addCollaboratorMutation.mutate(
            { orgid: Number(selectedOrgId) },
            {
                onSuccess: () => {
                    showAlert("Organización colaboradora añadida con éxito.");
                    setSelectedOrgId("");
                    setShowAddForm(false);
                },
                onError: (err) => {
                    showAlert(err?.response?.data?.error || "Error al registrar colaboradora.", "error");
                }
            }
        );
    };

    const handleDeleteCollaborator = (orgid, orgnom) => {
        deleteCollaboratorMutation.mutate(
            { orgid: Number(orgid) },
            {
                onSuccess: () => {
                    showAlert(`Se desvinculó a "${orgnom}" de la orden de servicio.`);
                },
                onError: (err) => {
                    showAlert(err?.response?.data?.error || "Error al remover organización.", "error");
                }
            }
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h6" fontWeight={800}>Organizaciones Autorizadas / Ejecutoras</Typography>
                </Box>
                <IconButton onClick={handleClose} disabled={isComponentBusy} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />

            <DialogContent dividers>
                {/* LISTADO DE ORGANIZACIONES ACTUALES */}
                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Empresas con autorización para visualizar y ejecutar viajes en esta Orden:
                </Typography>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell><b>Organización</b></TableCell>
                                <TableCell><b>RUC</b></TableCell>
                                <TableCell><b>Tipo</b></TableCell>
                                <TableCell align="center"><b>Rol</b></TableCell>
                                <TableCell align="center"><b>Acción</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isMasterLoading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell>
                                </TableRow>
                            ) : colaboradores.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>No hay registros consolidados.</TableCell>
                                </TableRow>
                            ) : (
                                colaboradores.map((colab) => {
                                    const isPrimary = colab.ordserorgpri === true || colab.OrdSerOrgPri === true;
                                    return (
                                        <TableRow key={colab.orgid} sx={{ bgcolor: isPrimary ? '#f0faff' : 'inherit' }}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={isPrimary ? 700 : 400}>
                                                    {colab.orgnom}
                                                </Typography>
                                                {colab.orgnomsoc && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {colab.orgnomsoc}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{colab.orgruc || "S/R"}</TableCell>
                                            <TableCell>
                                                <Chip label={colab.orgtip} size="small" variant="outlined" color={isPrimary ? "primary" : "default"} />
                                            </TableCell>
                                            <TableCell align="center">
                                                {isPrimary ? (
                                                    <Chip label="PROPIA (PRINCIPAL)" color="primary" size="small" sx={{ fontWeight: 700 }} />
                                                ) : (
                                                    <Chip label="COLABORADORA" variant="text" size="small" />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={isPrimary ? "La empresa propia no se puede eliminar" : "Remover acceso"}>
                                                    <span>
                                                        <IconButton
                                                            color="error"
                                                            size="small"
                                                            disabled={isPrimary || isComponentBusy}
                                                            onClick={() => handleDeleteCollaborator(colab.orgid, colab.orgnom)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* FORMULARIO DINÁMICO PARA ADICIONAR COLABORADORA */}
                {showAddForm ? (
                    <Box sx={{ p: 2, border: '1px dashed #1976d2', borderRadius: 2, bgcolor: '#fbfdff' }}>
                        <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1.5}>
                            Asignar Nueva Organización Proveedora
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2}>
                            <TextField
                                select
                                size="small"
                                label="Seleccione Proveedor"
                                fullWidth
                                disabled={isMasterLoading || isComponentBusy}
                                value={selectedOrgId}
                                onChange={(e) => setSelectedOrgId(e.target.value)}
                            >
                                <MenuItem value=""><em>Seleccione...</em></MenuItem>
                                {availableOptions.map((org) => (
                                    <MenuItem key={org.orgid} value={org.orgid}>
                                        {org.orgnom} {org.orgruc ? `(RUC: ${org.orgruc})` : ''}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddCircleIcon />}
                                onClick={handleAddCollaborator}
                                disabled={isComponentBusy || !selectedOrgId}
                                sx={{ height: 40, px: 3, minWidth: 150 }}
                            >
                                {isComponentBusy ? <CircularProgress size={20} color="inherit" /> : "Confirmar"}
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => { setShowAddForm(false); setSelectedOrgId(""); }}
                                disabled={isComponentBusy}
                                sx={{ height: 40 }}
                            >
                                Cancelar
                            </Button>
                        </Box>
                        {availableOptions.length === 0 && !isMasterLoading && (
                            <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
                                Todas las empresas proveedoras del sistema ya se encuentran asociadas a esta Orden de Servicio.
                            </Alert>
                        )}
                    </Box>
                ) : (
                    !isMasterLoading && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddCircleIcon />}
                            onClick={() => setShowAddForm(true)}
                            disabled={isComponentBusy}
                        >
                            Asignar Organización Colaboradora
                        </Button>
                    )
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="contained" color="grey" disabled={isComponentBusy}>
                    Cerrar Panel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CollaboratorsModal;